import {
  Component,
  EventEmitter,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
} from '@angular/core';
import { UntypedFormControl, UntypedFormGroup, FormGroupDirective, Validators } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { Subscription } from 'rxjs';
import { Project } from 'src/app/core/objects/project';
import { ProjectService } from 'src/app/core/services/project.service';
import { Load, LoadWithTranslatedName, StackingOptions } from 'src/app/core/types';
import { validateZero } from 'src/app/core/validators/validators';
import { FADE_IN_OUT_ANIMATION, HEIGHT_ANIMATION } from 'src/app/shared/animations/animations';
import { ColorsService } from '../../../../core/services/colors.service';
import { FormDataType } from '../../add-load.component';

@Component({
    selector: 'gl-load-dimensions',
    templateUrl: './load-dimensions.component.html',
    styleUrls: ['./load-dimensions.component.scss'],
    animations: [FADE_IN_OUT_ANIMATION, HEIGHT_ANIMATION],
    standalone: false
})
export class LoadDimensionsComponent implements OnInit, OnDestroy, OnChanges {
  @HostBinding('@show') public show;
  @Output() public submitForm = new EventEmitter<boolean>();
  @Output() public formData = new EventEmitter();
  @Output() public resetForm = new EventEmitter();
  @Output() public initialDimensions = new EventEmitter();
  @Output() public changeStackingOptions: EventEmitter<StackingOptions> = new EventEmitter<StackingOptions>();
  @Output() public layerCreateRequest = new EventEmitter<void>();
  @Output() public fillPalletRequest = new EventEmitter<void>();
  @Input() public chosenLoad: Load;
  @Input() public setActive: boolean;
  @Input() public highlightAddButton = false;
  @Input() public isLayerCalculationStale = false;
  @Input() public loads: LoadWithTranslatedName[];
  @Input() public currentType: number;
  @Input() public isPalletLoadingSpaceActive = false;
  @Input() public hasLoadedPalletLayers = false;
  @Input() public quantityLockedForLayers = false;
  @Input() public individualAddBlocked = false;
  @Input() public showPalletLayerActions = false;
  @Input() public showFillPalletAction = false;
  @Input() public isMyLoads = false;
  @Output() public changeLoadType = new EventEmitter<number>();
  @ViewChild('f', { static: false }) public formEl: FormGroupDirective;
  @HostBinding('class.checkboxActive') public checkboxActive = false;
  @HostBinding('class.highlightAdd') get isHighlightAdd(): boolean {
    return this.setActive || this.highlightAddButton;
  }
  @HostBinding('class.highlightRecalculate') get isHighlightRecalculate(): boolean {
    return this.isLayerCalculationStale;
  }
  public form: CustomForm;
  public saveLoadForm: SaveLoadForm;
  public project: Project;
  public isColorPicker: boolean;
  private subscriptions: Subscription[] = [];
  private isAddToFirst = false;
  public stackingOptions: StackingOptions;

  constructor(
    private translate: TranslateService,
    private projectService: ProjectService,
    private colorsService: ColorsService,
  ) {
    this.subscriptions.push(
      this.projectService.project.subscribe((project) => (this.project = project)),
    );
    this.form = this.generateLoadForm();
    this.form.disable();
  }

  ngOnInit(): void {
    this.stackingOptions = this.chosenLoad?.stackingOptions;
    this.saveLoadForm = new UntypedFormGroup({
      remember: new UntypedFormControl(false),
    });
    this.recalculateSumOfWeightsOnQuantityAndWeightChanges();
    this.recalculateWeightOnSumOfWeightsChanges();
    this.updateQuantityControlState();

    this.subscriptions.push(
      this.form.controls.load?.valueChanges.subscribe((value: CustomLoadForm['value']) => {
        const formData: FormDataType = {
          ...value,
          nameTranslated: value.name,
          loadType: this.currentType,
        };
        this.formData.emit(formData);
        const checkIsSavedLoad = this.isMyLoads
          ? false
          : this.loads.find((load: Load) => this.translate.instant(load.name) === value.name);
        this.checkboxActive = checkIsSavedLoad === undefined && value.name !== null;
      }),
    );
  }

  public saveLoadClick(event: Event): void {
    event.preventDefault();
    this.saveLoadForm.controls.remember.setValue(!this.saveLoadForm.controls.remember.value);
  }

  public loadArrangementChange(event: number): void {
    this.stackingOptions = {
      ...this.stackingOptions,
      formation: event,
    }
    this.changeStackingOptions.emit(this.stackingOptions);
  }

  public changeType(type: number): void {
    this.changeLoadType.emit(type);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (
      changes.isPalletLoadingSpaceActive ||
      changes.currentType ||
      changes.quantityLockedForLayers ||
      changes.hasLoadedPalletLayers ||
      changes.individualAddBlocked
    ) {
      this.updateQuantityControlState();
      this.updateSumOfWeightsControlState();
    }
    if (
      changes.chosenLoad?.currentValue &&
      changes.chosenLoad.currentValue !== changes.chosenLoad.previousValue
    ) {
      this.stackingOptions = changes.chosenLoad.currentValue.stackingOptions as StackingOptions;
      const quantity = this.chosenLoad.quantity > 0 ? this.chosenLoad.quantity : 1;
      this.form.controls.load.patchValue({
        name: this.translateLoadName(this.chosenLoad.name),
        length: this.chosenLoad.length,
        width: this.chosenLoad.width,
        color: this.chosenLoad.color
          ? this.chosenLoad.color
          : this.colorsService.getAvaliableColor(),
        height: this.chosenLoad.height,
        weight: this.chosenLoad.weight,
        diameter: this.chosenLoad.diameter,
        quantity,
        sumOfWeights:
          this.chosenLoad.weight > 0 ? Math.floor(this.chosenLoad.weight * quantity * 10) / 10 : 0,
      });
    }
  }

  public ngOnDestroy() {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  public openColorPicker() {
    this.isColorPicker = true;
  }

  public closeColorPicker() {
    this.isColorPicker = false;
  }

  public setColor(color: number) {
    this.form.controls.load.controls.color.setValue(color);
    this.closeColorPicker();
  }

  public get isAddBlocked(): boolean {
    return this.individualAddBlocked || this.isLayerCalculationStale;
  }

  public get isCreateLayerBlocked(): boolean {
    return !this.isLayerCalculationStale && !this.hasPositiveQuantity();
  }

  public onSubmit() {
    if (this.isAddBlocked) {
      return;
    }
    if (!this.chosenLoad?.diameter) {
      this.form.get('load').get('diameter').setValidators([]);
      this.form.updateValueAndValidity();
    }
    this.submitForm.emit(this.isAddToFirst);
  }

  public addToFirstHandler(): void {
    this.isAddToFirst = !this.isAddToFirst;
  }

  public onReset() {
    this.resetForm.emit();
  }

  public onInitialDimensions() {
    this.initialDimensions.emit();
  }

  public enableFormSafely(): void {
    this.form.enable({ emitEvent: false });
    this.updateSumOfWeightsControlState();
    this.updateQuantityControlState();
  }

  public setCalculatedQuantity(quantity: number): void {
    const control = this.form.controls.load.controls.quantity;
    const wasDisabled = control.disabled;
    if (wasDisabled) {
      control.enable({ emitEvent: false });
    }
    control.setValue(quantity, { emitEvent: true });
    this.updateQuantityControlState();
  }

  private shouldLockSumOfWeights(): boolean {
    return (
      this.isPalletLoadingSpaceActive &&
      (this.quantityLockedForLayers || this.hasLoadedPalletLayers)
    );
  }

  private updateSumOfWeightsControlState(): void {
    const control = this.form.controls.load?.controls?.sumOfWeights;
    if (!control || this.form.disabled) {
      return;
    }
    if (this.shouldLockSumOfWeights()) {
      control.disable({ emitEvent: false });
    } else if (control.disabled) {
      control.enable({ emitEvent: false });
    }
  }

  private updateQuantityControlState(): void {
    const control = this.form.controls.load?.controls?.quantity;
    if (!control) {
      return;
    }
    if (this.quantityLockedForLayers) {
      control.disable({ emitEvent: false });
    } else if (control.disabled && this.form.enabled) {
      control.enable({ emitEvent: false });
    }
  }

  private translateLoadName(loadName: string): string {
    return loadName ? (this.translate.instant(loadName) as string) : loadName;
  }

  private recalculateWeightOnSumOfWeightsChanges(): void {
    this.subscriptions.push(
      this.form.controls.load?.controls?.sumOfWeights.valueChanges.subscribe(
        (sumOfWeights: number) => {
          if (
            sumOfWeights !== null &&
            this.isQuantityAndWeightAndSumOfWeightsValid() &&
            this.hasPositiveQuantity()
          ) {
            this.form.controls.load.controls.weight.setValue(
              Math.floor((sumOfWeights / this.getQuantityValue()) * 10) / 10,
              { emitEvent: false },
            );
          }
        },
      ),
    );
  }

  private recalculateSumOfWeightsOnQuantityAndWeightChanges(): void {
    this.subscriptions.push(
      this.form?.controls.load?.controls?.quantity.valueChanges.subscribe((quantity: number) => {
        if (quantity !== null && this.isQuantityAndWeightAndSumOfWeightsValid()) {
          const weight = Number(this.form.controls.load.controls.weight.value);
          this.form.controls.load.controls.sumOfWeights.setValue(
            Math.floor(weight * quantity * 10) / 10,
            { emitEvent: false },
          );
        }
      }),
    );
    this.subscriptions.push(
      this.form?.controls.load?.controls?.weight.valueChanges.subscribe((weight: number) => {
        if (weight !== null && this.isQuantityAndWeightAndSumOfWeightsValid()) {
          this.form.controls.load.controls.sumOfWeights.setValue(
            Math.floor(this.getQuantityValue() * weight * 10) / 10,
            { emitEvent: false },
          );
        }
      }),
    );
  }

  public createLayer(event?: Event): void {
    if (!this.showPalletLayerActions || this.isCreateLayerBlocked) {
      return;
    }
    if (event) {
      event.stopPropagation();
    }
    this.layerCreateRequest.emit();
  }

  public fillPallet(event?: Event): void {
    if (!this.showPalletLayerActions || !this.showFillPalletAction) {
      return;
    }
    if (event) {
      event.stopPropagation();
    }
    this.fillPalletRequest.emit();
  }

  private isQuantityAndWeightAndSumOfWeightsValid(): boolean {
    return (
      this.form.controls.load.controls.quantity.valid &&
      this.form.controls.load.controls.weight.valid
    );
  }

  private getQuantityValue(): number {
    return parseFloat(String(this.form.controls.load.controls.quantity.value ?? ''));
  }

  private hasPositiveQuantity(): boolean {
    const quantity = this.getQuantityValue();
    return Number.isFinite(quantity) && quantity > 0;
  }

  private generateLoadForm() {
    return new UntypedFormGroup({
      load: new UntypedFormGroup({
        name: new UntypedFormControl('', [Validators.maxLength(255)]),
        length: new UntypedFormControl(null, [Validators.required, validateZero]),
        width: new UntypedFormControl(null, [Validators.required, validateZero]),
        height: new UntypedFormControl(null, [Validators.required, validateZero]),
        weight: new UntypedFormControl(null),
        quantity: new UntypedFormControl(null, [Validators.required, validateZero]),
        sumOfWeights: new UntypedFormControl(null),
        color: new UntypedFormControl(0),
        diameter: new UntypedFormControl(null, [Validators.required, validateZero]),
      }),
    }) as CustomForm;
  }
}

export interface CustomForm extends UntypedFormGroup {
  value: {
    load: CustomLoadForm;
  };

  controls: {
    load: CustomLoadForm;
  };
}

interface CustomLoadForm extends UntypedFormGroup {
  value: {
    name: string;
    length: number;
    width: number;
    height: number;
    weight: number;
    quantity: number;
    sumOfWeights: number;
    diameter: number;
    color: number;
  };
}

interface SaveLoadForm extends UntypedFormGroup {
  value: {
    remember: boolean;
  };
}
